import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Vilkår.css';

const Personvern = () => {
  const [deletionEmail, setDeletionEmail] = useState('');
  const [deletionReason, setDeletionReason] = useState('');
  const [deletionMessage, setDeletionMessage] = useState('');
  const [deletionError, setDeletionError] = useState('');
  const [deletionSending, setDeletionSending] = useState(false);

  const handleDeletionSubmit = async (e) => {
    e.preventDefault();
    setDeletionError('');
    setDeletionMessage('');
    setDeletionSending(true);
    const form = e.target;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/privacy/request-data-deletion`,
        {
          email: deletionEmail.trim(),
          reason: deletionReason.trim(),
          website: form.website?.value ?? '',
          email_confirm: form.email_confirm?.value ?? '',
          _submission_time: Date.now().toString(),
        },
        { withCredentials: true }
      );
      if (response.data.success) {
        setDeletionMessage(response.data.message);
        setDeletionEmail('');
        setDeletionReason('');
      } else {
        setDeletionError(response.data.message || 'Noe gikk galt.');
      }
    } catch (err) {
      setDeletionError(err.response?.data?.message || 'Kunne ikke sende forespørselen. Prøv igjen eller kontakt support@lopeglad.no.');
    } finally {
      setDeletionSending(false);
    }
  };

  return (
    <div className="vilkår-page">
      <div className="vilkår-container">
        <div className="vilkår-header">
          <Link to="/" className="back-link">
            <span className="arrow">←</span> Tilbake
          </Link>
          <h1>Personvern</h1>
        </div>

        <div className="vilkår-content">
          <section className="vilkår-section">
            <h2>1. INTRODUKSJON</h2>
            <p>
              Denne personvernerklæringen beskriver hvordan Løpeappen («vi», «oss», «vår») samler inn, bruker og beskytter 
              dine personopplysninger når du bruker vår mobilapplikasjon («Appen»).
            </p>
            <p>
              Vi er opptatt av å beskytte ditt personvern og behandler dine personopplysninger i samsvar med gjeldende 
              personvernlovgivning, inkludert EUs personvernforordning (GDPR) og norsk personvernlovgivning.
            </p>
          </section>

          <section className="vilkår-section">
            <h2>2. INFORMASJON VI SAMLER INN</h2>
            
            <h3>2.1 Opplysninger du gir oss direkte</h3>
            <p>Vi samler inn følgende personopplysninger fra deg:</p>
            <ul>
              <li>E-postadresse (obligatorisk for å opprette konto)</li>
              <li>Telefonnummer (frivillig)</li>
              <li>Kjønn (for statistiske formål og personalisering av treningsprogrammer)</li>
              <li>Alder (for statistiske formål og personalisering av treningsprogrammer)</li>
            </ul>

            <h3>2.2 Informasjon vi ikke samler inn</h3>
            <p>Vi samler ikke inn:</p>
            <ul>
              <li>Stedslokasjon</li>
              <li>Helsedata eller medisinske opplysninger</li>
              <li>Data utenfor appen</li>
              <li>Informasjon om dine enheter utover det som er nødvendig for at appen skal fungere</li>
            </ul>
          </section>

          <section className="vilkår-section">
            <h2>3. HVORDAN VI BRUKER DINE PERSONOPPLYSNINGER</h2>
            <p>Vi bruker dine personopplysninger til følgende formål:</p>
            
            <h3>3.1 Levering av tjenesten</h3>
            <ul>
              <li>For å opprette og administrere din brukerkonto</li>
              <li>For å tilpasse treningsprogrammer basert på din alder og kjønn</li>
              <li>For å kommunisere med deg om ditt abonnement og endringer i tjenesten</li>
            </ul>

            <h3>3.2 Forbedring av tjenesten</h3>
            <ul>
              <li>For å analysere bruksmønstre og forbedre funksjoner i appen</li>
              <li>For å løse tekniske problemer og feilsøking</li>
            </ul>

            <h3>3.3 Kommunikasjon</h3>
            <ul>
              <li>For å sende deg viktig informasjon om ditt abonnement, inkludert fornyelsesvarsel</li>
              <li>For å besvare dine henvendelser og kundeserviceforespørsler</li>
            </ul>
          </section>

          <section className="vilkår-section">
            <h2>4. DELING AV INFORMASJON</h2>
            
            <h3>4.1 Ingen deling med tredjeparter</h3>
            <p>
              Vi selger ikke, leier ut eller på annen måte deler dine personopplysninger med tredjeparter for kommersielle formål.
            </p>

            <h3>4.2 Tjenesteleverandører</h3>
            <p>
              Vi kan bruke tjenesteleverandører som hjelper oss med å levere tjenestene våre (for eksempel betalingsbehandlere, 
              e-postleverandører, skylagringstjenester). Disse tjenesteleverandørene har kun tilgang til dine personopplysninger 
              i den grad det er nødvendig for å utføre tjenester på våre vegne og er forpliktet til å ikke bruke eller dele dine 
              opplysninger til andre formål.
            </p>

            <h3>4.3 Juridiske forpliktelser</h3>
            <p>
              Vi kan utlevere dine personopplysninger hvis det er påkrevd ved lov eller i god tro på at slik handling er nødvendig for å:
            </p>
            <ul>
              <li>Overholde juridiske forpliktelser</li>
              <li>Beskytte og forsvare våre rettigheter eller eiendom</li>
              <li>Beskytte den personlige sikkerheten til appbrukere eller offentligheten</li>
            </ul>
          </section>

          <section className="vilkår-section">
            <h2>5. SIKKERHET</h2>
            <p>
              Vi tar sikringen av dine personopplysninger på alvor og implementerer passende tekniske og organisatoriske tiltak 
              for å beskytte dine personopplysninger mot uautorisert tilgang, endring, utlevering eller ødeleggelse.
            </p>
          </section>

          <section className="vilkår-section">
            <h2>6. OPPBEVARING AV DATA</h2>
            <p>
              Vi beholder dine personopplysninger så lenge det er nødvendig for å oppfylle formålene beskrevet i denne 
              personvernerklæringen, med mindre en lengre oppbevaringsperiode er påkrevd eller tillatt ved lov. Når du sletter 
              kontoen din, vil vi slette eller anonymisere dine personopplysninger innen 30 dager, med mindre vi er pålagt å beholde 
              noen opplysninger av juridiske årsaker.
            </p>
          </section>

          <section className="vilkår-section">
            <h2>7. DINE RETTIGHETER</h2>
            <p>I henhold til gjeldende personvernlovgivning har du følgende rettigheter:</p>
            <ul>
              <li>
                <strong>Rett til innsyn:</strong> Du har rett til å få en bekreftelse på om vi behandler personopplysninger om deg, 
                og i så fall, få tilgang til disse personopplysningene.
              </li>
              <li>
                <strong>Rett til retting:</strong> Du har rett til å få rettet unøyaktige personopplysninger om deg.
              </li>
              <li>
                <strong>Rett til sletting:</strong> Du har i visse tilfeller rett til å få slettet personopplysningene vi har om deg.
              </li>
              <li>
                <strong>Rett til begrensning av behandling:</strong> Du har i visse tilfeller rett til å begrense behandlingen av 
                dine personopplysninger.
              </li>
              <li>
                <strong>Rett til dataportabilitet:</strong> Du har rett til å motta personopplysningene om deg i et strukturert, 
                alminnelig anvendt og maskinlesbart format.
              </li>
              <li>
                <strong>Rett til å protestere:</strong> Du har rett til å protestere mot behandling av dine personopplysninger i 
                visse tilfeller.
              </li>
              <li>
                <strong>Rett til å trekke tilbake samtykke:</strong> Hvis behandlingen er basert på samtykke, har du rett til å 
                trekke samtykket tilbake når som helst.
              </li>
            </ul>
            <p>
              For å utøve noen av disse rettighetene, vennligst kontakt oss på{' '}
              <a href="mailto:support@lopeglad.no">support@lopeglad.no</a>
            </p>

            <h3>Be om sletting av bruker og data</h3>
            <p>
              Du kan be om at vi sletter din bruker og tilknyttede data. Fyll ut e-postadressen din under så tar vi forespørselen videre. Vi behandler den i tråd med vår personvernerklæring.
            </p>
            <form onSubmit={handleDeletionSubmit} className="personvern-deletion-form">
              {deletionMessage && <div className="personvern-deletion-success">{deletionMessage}</div>}
              {deletionError && <div className="personvern-deletion-error">{deletionError}</div>}
              <label htmlFor="personvern-deletion-email" className="personvern-deletion-label">E-postadresse knyttet til kontoen</label>
              <input
                id="personvern-deletion-email"
                type="email"
                name="email"
                placeholder="din@epost.no"
                value={deletionEmail}
                onChange={(e) => setDeletionEmail(e.target.value)}
                required
                autoComplete="email"
                className="personvern-deletion-input"
                disabled={deletionSending}
              />
              <label htmlFor="personvern-deletion-reason" className="personvern-deletion-label">Hvorfor ønsker du å slette brukeren og dataen? (valgfritt)</label>
              <textarea
                id="personvern-deletion-reason"
                name="reason"
                placeholder="F.eks. bruker ikke appen lenger, ønsker ikke å ha konto, osv."
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value)}
                rows={3}
                className="personvern-deletion-textarea"
                disabled={deletionSending}
              />
              <div className="personvern-deletion-honeypot" aria-hidden="true">
                <input type="text" name="website" tabIndex={-1} autoComplete="off" defaultValue="" className="personvern-deletion-honeypot-input" />
                <input type="text" name="email_confirm" tabIndex={-1} autoComplete="off" defaultValue="" className="personvern-deletion-honeypot-input" />
              </div>
              <button type="submit" className="personvern-deletion-submit" disabled={deletionSending}>
                {deletionSending ? 'Sender...' : 'Send forespørsel om sletting'}
              </button>
            </form>
          </section>

          <section className="vilkår-section">
            <h2>8. INFORMASJONSKAPSLER (COOKIES)</h2>
            <p>
              Appen bruker ikke informasjonskapsler, men kan bruke lignende teknologier for å lagre visse preferanser lokalt på 
              din enhet for å forbedre brukeropplevelsen.
            </p>
          </section>

          <section className="vilkår-section">
            <h2>9. BARN</h2>
            <p>
              Appen er ikke rettet mot barn under 16 år, og vi samler ikke bevisst inn personopplysninger fra barn under 16 år. 
              Hvis du er en forelder eller verge og tror at ditt barn har gitt oss personopplysninger, vennligst kontakt oss, og 
              vi vil gjøre vårt beste for å slette slik informasjon fra våre systemer.
            </p>
          </section>

          <section className="vilkår-section">
            <h2>10. ENDRINGER I PERSONVERNERKLÆRINGEN</h2>
            <p>
              Vi kan oppdatere vår personvernerklæring fra tid til annen. Når vi gjør endringer, vil vi informere deg ved å 
              oppdatere datoen øverst i denne personvernerklæringen og ved å gi deg en melding i appen eller via e-post før 
              endringen trer i kraft.
            </p>
          </section>

          <section className="vilkår-section">
            <h2>11. KONTAKTINFORMASJON</h2>
            <p>
              Hvis du har spørsmål eller bekymringer om vår personvernerklæring eller databehandling, vennligst kontakt oss på:
            </p>
            <ul>
              <li>E-post: <a href="mailto:support@lopeglad.no">support@lopeglad.no</a></li>
            </ul>
          </section>

          <section className="vilkår-section">
            <h2>12. KLAGERETT</h2>
            <p>
              Hvis du mener at vår behandling av dine personopplysninger ikke overholder personvernlovgivningen, har du rett til 
              å klage til Datatilsynet (
              <a href="https://www.datatilsynet.no" target="_blank" rel="noopener noreferrer">www.datatilsynet.no</a>).
            </p>
            <p className="last-updated">Sist oppdatert: 02.05.2025</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Personvern;

